<template>
  <button
    class="VisionSwatch"
    :class="[
      `VisionSwatch--size-${props.size}`,
      `VisionSwatch--style-${visualStyle}`,
      {
        [`VisionSwatch--mobile-size-${props.mobileSize}`]: !!props.mobileSize,
        'VisionSwatch--is-active': props.isActive,
        'VisionSwatch--full-width': props.fullWidth,
      },
    ]"
    :disabled="props.disabled"
    @click="!props.disabled ? $emit('select') : null"
  >
    <span
      v-if="props.badgeText"
      class="VisionSwatch__limited-edition"
    >{{ props.badgeText }}</span>
    <div class="VisionSwatch__outer">
      <span
        class="VisionSwatch__inner"
        :class="{
          'VisionSwatch__inner--image-swatch': isImageSwatch,
          'VisionSwatch__inner--rotated':
            props.swatches?.length > 1 && !props.swatchIcon,
        }"
      >
        <VisionIcon
          v-if="props.iconOverlay"
          :icon="props.iconOverlay"
          class="VisionSwatch__icon"
          size="stretch"
        />

        <span
          v-if="props.disabled"
          class="VisionSwatch__disabled"
        />
        
        <span
          v-if="props.overlayText && props.swatches?.length > 1"
          class="VisionSwatch__pre-order"
        >{{ props.overlayText }}</span>

        <template
          v-if="!props.swatchIcon"
        >
          <span
            v-for="(swatch, index) in props.swatches"
            :key="index"
            class="VisionSwatch__colour"
            :class="[
              {
                'VisionSwatch__colour--secondary': index === 1,
              },
            ]"
            :style="(swatch || {}).styles"
          >
            <span
              v-if="props.overlayText && props.swatches?.length <= 1"
              class="
              VisionSwatch__pre-order
              VisionSwatch__pre-order--single-colour"
            >{{ props.overlayText }}</span>

            <img
              v-if="(swatch || {}).image"
              class="VisionSwatch__image"
              :src="(swatch || {}).image"
              :loading="props.lazyLoadImages ? 'lazy' : 'eager'"
            />
          </span>
        </template>
        <img
          v-else
          class="VisionSwatch__image"
          :src="props.swatchIcon"
          :loading="props.lazyLoadImages ? 'lazy' : 'eager'"
        />
      </span>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IconName } from '../../config/icons.config'
import VisionIcon from '../VisionIcon/VisionIcon.vue'

interface Swatch {
  styles: string
  image: string
}

interface VisionSwatchProps {
  swatches: Array<Swatch>
  iconOverlay?: '' | IconName
  size?: 'x-small' | 'small' | 'regular' | 'medium' | 'large'
  mobileSize?: '' | 'xx-small' | 'x-small' | 'regular' | 'medium' | 'large'
  swatchIcon?: string
  isActive?: boolean
  disabled?: boolean
  fullWidth?: boolean
  overlayText?: string
  badgeText?: string
  lazyLoadImages?: boolean
}

const props = withDefaults(defineProps<VisionSwatchProps>(), {
  swatches: () => [],
  iconOverlay: '',
  size: 'regular',
  mobileSize: '',
  isActive: false,
  disabled: false,
  fullwidth: false,
  overlayText: '',
  badgeText: '',
  swatchIcon: '',
  lazyLoadImages: true
})

const visualStyle = computed(() => {
  if (props.size == 'large' || props.fullWidth) {
    return 'large'
  } else {
    return 'default'
  }
})

const isImageSwatch = computed(() => {
  return (!!props.swatches[0]?.image && !props.swatches[1])
})

defineEmits(['select'])
</script>

<style lang="postcss">
.VisionSwatch {
  @apply text-white relative;
}

.VisionSwatch__outer {
  @apply relative
    w-6 h-6;
    
    &::before {
     @apply content-[''] absolute top-0 left-0
      w-full h-full
      border-solid border opacity-0 rounded-full
      transition-opacity duration-200 ease-in-out;
      border-color: var(--vision-swatch-border-color, theme('colors.off-black'));
    }

  .VisionSwatch--full-width & {
    @apply w-full h-full;
  }
}

.VisionSwatch:hover .VisionSwatch__outer,
.VisionSwatch--is-active .VisionSwatch__outer {
  @apply before:opacity-100;
}

.VisionSwatch__inner {
  @apply relative
    flex
    w-full h-full
    overflow-hidden
    rounded-full
    transition-transform duration-200 ease-in-out;

  &::after {
    @apply content-[''] absolute w-full h-full border
    rounded-full opacity-50 mix-blend-overlay pointer-events-none;
    border-color: var(--vision-swatch-border-color, theme('colors.black'));
  }

  &--image-swatch {
    @apply rotate-0;
  }

  .VisionSwatch:not([disabled]):hover &,
  .VisionSwatch--is-active & {
    @apply scale-75;
  }

  .VisionSwatch--size-large:not([disabled]):hover &,
  .VisionSwatch--size-large.VisionSwatch--is-active & {
    @apply scale-[0.8];
  }
}

.VisionSwatch__colour {
  @apply relative
    w-1/2
    grow
    overflow-hidden;
}

.VisionSwatch__image {
  @apply absolute inset-0 w-full h-full object-cover;
}

.VisionSwatch__icon {
  @apply absolute top-1/2 left-1/2 z-[2]
  w-[50%] origin-center -translate-x-1/2 -translate-y-1/2;
  filter: drop-shadow(0px 0px 2px rgba(0,0,0,0.5))
}

/*
  Modifiers

  --rotated (two swatches)
  --size
  --visual-style
  --disabled
  --pre-order
  --limited-edition
*/

/* Rotated */
.VisionSwatch__inner--rotated {
  @apply -rotate-[135deg];

  .VisionSwatch__disabled {
    @apply rotate-0;
  }

  .VisionSwatch__pre-order, .VisionSwatch__icon {
    @apply rotate-[135deg] origin-center;
  }
}

/* Size */
.VisionSwatch--full-width {
  @apply w-full h-full
    absolute top-0 left-0;
}

.VisionSwatch--size-x-small {
  .VisionSwatch__outer {
    @apply w-4 h-4;
  }

  &.VisionSwatch--mobile-size-xx-small {
    .VisionSwatch__outer {
      @apply w-3 h-3
        lg:w-4 lg:h-4;
    }
  }
}

.VisionSwatch--size-small {
  .VisionSwatch__outer {
    @apply w-6 h-6;
  }

  &.VisionSwatch--mobile-size-x-small {
    .VisionSwatch__outer {
      @apply w-4 h-4
        xl:w-6 xl:h-6;
    }
  }
}

.VisionSwatch--size-medium {
  .VisionSwatch__outer {
    @apply w-8 h-8;
  }

  &.VisionSwatch--mobile-size-large {
    .VisionSwatch__outer {
      @apply w-10 h-10 xl:w-8 xl:h-8;
    }
  }
}

.VisionSwatch--size-medium {
  .VisionSwatch__outer {
    @apply w-8 h-8;
  }

  &.VisionSwatch--mobile-size-large {
    .VisionSwatch__outer {
      @apply w-10 h-10 xl:w-8 xl:h-8;
    }
  }
}

.VisionSwatch--size-large {
  .VisionSwatch__outer {
    @apply w-10 h-10;
  }

  &.VisionSwatch--mobile-size-medium {
    .VisionSwatch__outer {
      @apply w-8 h-8 xl:w-10 xl:h-10;
    }
  }
}

/* Visual Style */
.VisionSwatch--style-large {
  .VisionSwatch__outer::before {
    @apply transition-all;
    background-color: var(--vision-swatch-large-background-color, theme('colors.white'));
    border-color: var(--vision-swatch-large-border-color, theme('colors.grey.300'));
  }

  &:not([disabled]):hover, &.VisionSwatch--is-active {
    .VisionSwatch__inner {
      @apply scale-[0.9];
    }

    .VisionSwatch__outer::before {
      @apply scale-[1.25];
    }
  }

  &.VisionSwatch--is-active {
    .VisionSwatch__outer::before {
      border-color: var(--vision-swatch-large-active-border-color, theme('colors.grey.DEFAULT'));
    }
  }
}

/* Disabled */
.VisionSwatch[disabled] {
  @apply cursor-not-allowed;

  .VisionSwatch__colour, .VisionSwatch__image {
    @apply opacity-50;
  }
}

.VisionSwatch__disabled {
  @apply absolute w-full h-full
  flex items-center justify-center
  z-[4] rounded-full border border-grey-300
  rotate-45;
  border-color: var(--vision-swatch-disabled-border-color, theme('colors.grey.300'));
  filter: drop-shadow(0px 0px 2px rgba(0,0,0,0.24));

  &::before, &::after {
    @apply content-[''] w-full h-0.5 bg-grey-300;
  }

  &::after {
    @apply absolute rotate-90 -mt-px;
  }
}

/* Pre-Order */
.VisionSwatch__pre-order {
  @apply absolute z-[2] top-1/2 left-1/2
  origin-center -translate-x-1/2 -translate-y-1/2
  text-[7px] uppercase leading-[1] tracking-wider font-semibold;
}

/* Limited Edition */
.VisionSwatch__limited-edition {
  @apply absolute top-0 z-[2] -right-2 h-[20px] w-[20px]
  bg-gradient rounded-full border-2
  text-[9px] leading-none font-semibold flex items-center justify-center;
  border-color: var(--vision-swatch-limited-edition-border-color, theme('colors.white'));
}
</style>
